import { makeAutoObservable, runInAction } from 'mobx'
import { MultiValue } from 'react-select'
import { Loaders, Filters, FilterOptions, FormData } from './type'
import { InventoryService } from '~/shared/api/services/inventory'
import {
  Inventory,
  Product,
  ProductById,
  ProductVariation,
} from '~/shared/api/services/inventory/type'
import {
  convertFilters,
  handleAxiosError,
  mapDataForSelect,
} from '~/shared/lib'
import { SelectOption } from '~/shared/ui'
import { GlobalService } from '~/shared/api/services/global'

export class InventoryStore {
  loaders: Loaders = {
    inventory: false,
    loadMoreInventory: false,
    products: false,
    manufacturers: false,
    warehouses: false,
    productById: false,
    createInventory: false,
    inventoryById: false,
    deleteInventory: false,
    updateInventory: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  filters: Filters = {
    products: [],
    manufacturers: [],
    warehouses: [],
    states: [],
    dates: [],
  }

  formData: FormData = {
    warehouse: null,
    product: null,
    comment: '',
  }

  filterOptions: FilterOptions = {
    products: [],
    manufacturers: [],
    warehouses: [],
  }

  createInventoryData: ProductById[] = []

  stateOptions = [
    { value: 'completed', label: 'Completed' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'created', label: 'Created' },
  ]

  inventoryById: Product | null = null

  inventoryList: Inventory[] = []

  variations: ProductVariation[] = []

  constructor() {
    makeAutoObservable(this)
  }

  activeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof Loaders) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  getInventory = async (action?: 'loadMoreInventory') => {
    if (action === 'loadMoreInventory' && this.requestSettings.isAllLoaded)
      return
    this.activeLoader(action || 'inventory')
    try {
      const filterParams = convertFilters(this.filters)
      const params = {
        limit: this.requestSettings.limit,
        offset:
          action !== 'loadMoreInventory' ? 0 : this.requestSettings.offset,
        ...filterParams,
      }
      const { data } = await InventoryService.getInventory({ params })

      runInAction(() => {
        this.inventoryList =
          action === 'loadMoreInventory'
            ? [...this.inventoryList, ...data]
            : data

        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action !== 'loadMoreInventory'
              ? this.requestSettings.limit
              : this.requestSettings.offset + this.requestSettings.limit,
        }
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader(action || 'inventory')
      })
    }
  }

  setFilters = (
    name: keyof Filters,
    value: string[] | MultiValue<SelectOption>,
  ) => {
    this.filters = { ...this.filters, [name]: value }
  }

  setFormData = <K extends keyof FormData>(name: K, value: FormData[K]) => {
    this.formData[name] = value
  }

  getProducts = async (q?: string) => {
    try {
      this.activeLoader('products')
      const params = q ? { params: { q } } : {}

      const { data } = await InventoryService.getProducts(params)
      runInAction(() => {
        this.filterOptions = {
          ...this.filterOptions,
          products: mapDataForSelect(data, 'product_id', 'name'),
        }
      })
    } finally {
      runInAction(() => {
        this.closeLoader('products')
      })
    }
  }

  getManufacturers = async (q?: string) => {
    try {
      this.activeLoader('manufacturers')
      const params = q ? { params: { q } } : {}

      const { data } = await GlobalService.getManufacturers(params)
      runInAction(() => {
        this.filterOptions = {
          ...this.filterOptions,
          manufacturers: mapDataForSelect(data, 'id', 'name'),
        }
      })
    } finally {
      runInAction(() => {
        this.closeLoader('manufacturers')
      })
    }
  }

  getWarehouses = async (q?: string) => {
    try {
      this.activeLoader('warehouses')
      const params = q ? { params: { q } } : {}

      const { data } = await InventoryService.getWarehouses(params)
      runInAction(() => {
        const mapperData = mapDataForSelect(data, 'id', 'name')
        this.filterOptions = {
          ...this.filterOptions,
          warehouses: mapperData,
        }
        if (mapperData.length === 1) {
          this.setFormData('warehouse', mapperData[0])
        }
      })
    } finally {
      runInAction(() => {
        this.closeLoader('warehouses')
      })
    }
  }

  getProductById = async (id: string) => {
    try {
      this.activeLoader('productById')

      const { data } = await InventoryService.getProductById(id)
      runInAction(() => {
        this.createInventoryData = [...this.createInventoryData, data]
        this.formData = { ...this.formData, product: null }
      })
    } finally {
      runInAction(() => {
        this.closeLoader('productById')
      })
    }
  }

  removeProductByCreate = (id: string) => {
    this.createInventoryData = this.createInventoryData.filter(
      (el) => el.id !== id,
    )
  }

  createNewInventory = async () => {
    try {
      this.activeLoader('createInventory')
      await InventoryService.createInventory({
        warehouseId: this.formData.warehouse?.value || null,
        comment: this.formData.comment || null,
        products: this.createInventoryData.map((product) => ({
          ...product,
          availability: product.availability || null,
          brand: {
            ...product.brand,
            name: product.brand.name || null,
          },
          description: product.description || null,
          id: product.id || null,
          images: product.images.map((image) => ({
            ...image,
            c1x1: image.c1x1 || null,
            c300: image.c300 || null,
            original: image.original || null,
            thumb: image.thumb || null,
          })),
          manufacturer: {
            ...product.manufacturer,
            id: product.manufacturer.id || null,
            legal_entity_id: product.manufacturer.legal_entity_id || null,
            name: product.manufacturer.name || null,
          },
          name: product.name || null,
        })),
      })
    } catch (errors) {
      const errorMessage = handleAxiosError(errors)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('createInventory')
    }
  }

  resetFilters = () => {
    this.filters = {
      products: [],
      manufacturers: [],
      warehouses: [],
      states: [],
      dates: [],
    }
  }

  clearformData = () => {
    this.formData = {
      warehouse: null,
      product: null,
      comment: '',
    }
    this.createInventoryData = []
  }

  setInventoryFromApi = (data: Product) => {
    runInAction(() => {
      this.inventoryById = data
      this.variations = data.variations.map((el) => ({
        ...el,
        count: el.count === null ? '' : el.count,
      }))
    })
  }

  setVariations = (id: string, newValue: string) => {
    this.variations = this.variations.map((el) =>
      el.variationId === id
        ? { ...el, count: newValue === '' ? '' : +newValue }
        : el,
    )
  }

  deleteInventory = async (id: string) => {
    this.activeLoader('deleteInventory')

    try {
      await InventoryService.deleteInventory(id as string)
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      runInAction(() => {
        this.closeLoader('deleteInventory')
      })
    }
  }

  updateInventoryState = async (targetState: 'completed' | 'in_progress') => {
    this.activeLoader('updateInventory')
    try {
      if (this.inventoryById) {
        await InventoryService.updateInventoryState({
          id: this.inventoryById.id,
          state: targetState,
          variations: this.variations.map((variation) => ({
            ...variation,
            variationId: variation.variationId || null,
            count:
              variation.count === '' || variation.count == null
                ? null
                : Number(variation.count),
            details: {
              ...variation.details,
              productId: variation.details.productId || null,
              name: variation.details.name || null,
              variationId: variation.details.variationId || null,
              vendorCode: variation.details.vendorCode || null,
              brandName: variation.details.brandName || null,
              manufacturerName: variation.details.manufacturerName || null,
              manufacturerId: variation.details.manufacturerId || null,
              optionsText: variation.details.optionsText || null,
              optionImage: variation.details.optionImage || null,
              options: variation.details.options.map((option) => ({
                ...option,
                optionName: option.optionName || null,
                valueName: option.valueName || null,
                valueSizes: option.valueSizes
                  ? {
                      sizeUs: option.valueSizes.sizeUs || null,
                      sizeInternational:
                        option.valueSizes.sizeInternational || null,
                      sizeRu: option.valueSizes.sizeRu || null,
                      sizeInches: option.valueSizes.sizeInches || null,
                      sizeCentimeters:
                        option.valueSizes.sizeCentimeters || null,
                    }
                  : null,
              })),
              image: variation.details.image
                ? {
                    ...variation.details.image,
                    c1x1: variation.details.image.c1x1 || null,
                    c300: variation.details.image.c300 || null,
                    thumb: variation.details.image.thumb || null,
                    original: variation.details.image.original || null,
                    o600: variation.details.image.o600 || null,
                    o1200: variation.details.image.o1200 || null,
                    optionValues: variation.details.image.optionValues || null,
                  }
                : null,
            },
          })),
        })

        runInAction(() => {
          if (this.inventoryById) {
            this.inventoryById.state = targetState
          }
        })
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('updateInventory')
    }
  }
}

export default InventoryStore
